"use client";

import { Checkbox, Link } from "@heroui/react";
import { useEffect, useState } from "react";
import ModalError from "./ModalError";
import qrCreate from "@utils/qrCreate";
import { SUBSCRIPTION_OPTIONS } from "@utils/subscriptions";
import { Smartphone } from "lucide-react";
import { Button } from "@/components/platform/v2/ui/button";

const emptyStatus = { entered: false } as any;

export default function SignInQrForm({
  code,
  link,
  utmCompany = {},
  checkQrStatus,
  signinByEmail,
  signupCaptcha,
}: {
  code: string;
  link: string;
  utmCompany?: Record<string, string | string[] | undefined>;
  checkQrStatus: Function;
  signinByEmail: Function;
  signupCaptcha: Function;
}) {
  const [status, setStatus] = useState(emptyStatus);
  const [informMessage, setInformMessage] = useState(true);
  const [vebinarMessage, setVebinarMessage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const subscriptions = [
    { ...SUBSCRIPTION_OPTIONS[0], subscribe: informMessage },
    { ...SUBSCRIPTION_OPTIONS[1], subscribe: vebinarMessage },
  ];

  const [qr, setQr] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const pwaUser = status?.user || {};
  const pwaCard = pwaUser?.card || {};
  const pwaName =
    pwaCard?.name ||
    [pwaCard?.first_name, pwaCard?.last_name].filter(Boolean).join(" ") ||
    pwaUser?.first_name ||
    status?.auth?.name ||
    "";
  const pwaMail =
    pwaCard?.mail || status?.userLogin || status?.auth?.email || "";
  const pwaAvatar = pwaUser?.photo_url || pwaCard?.photo_url || "";
  const authProjects = (
    Array.isArray(status?.projects) && status.projects.length
      ? status.projects
      : status?.auth
        ? [{ auth: status.auth, pwaProps: status.pwaProps || {} }]
        : []
  ).filter((project: any) => project?.auth?.email);
  const selectedProject =
    authProjects.find(
      (project: any) =>
        String(project?.auth?._id || project?.auth?.email) ===
        selectedProjectId,
    ) ||
    authProjects[0] ||
    {};
  const selectedAuth = selectedProject?.auth || {};
  const selectedPwaProps = selectedProject?.pwaProps || {};
  const hasAuth = authProjects.length > 0;
  const paidProject = authProjects.find((project: any) => {
    const tarif = String(project?.auth?.tarif || "").toLowerCase();
    return Boolean(tarif && tarif !== "demo");
  });
  const hasPaidProject = Boolean(paidProject);

  const rememberPwaProfile = (projectEmail: string) => {
    window.localStorage.setItem(
      "vcentre-pwa-profile",
      JSON.stringify({
        projectEmail: String(projectEmail || "")
          .trim()
          .toLowerCase(),
        name: pwaName,
        mail: pwaMail,
        avatar: pwaAvatar,
      }),
    );
  };

  const getNextDemoEmail = (email: string) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();
    const atIndex = normalizedEmail.lastIndexOf("@");
    if (atIndex <= 0 || atIndex === normalizedEmail.length - 1) return "";

    const localPart = normalizedEmail.slice(0, atIndex);
    const domain = normalizedEmail.slice(atIndex + 1);
    const escapeRegExp = (value: string) =>
      value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const demoEmailPattern = new RegExp(
      `^${escapeRegExp(localPart)}\\+(\\d+)@${escapeRegExp(domain)}$`,
      "i",
    );
    const maxIndex = authProjects.reduce((currentMax: number, project: any) => {
      const index = Number(
        String(project?.auth?.email || "").match(demoEmailPattern)?.[1] || 0,
      );
      return Math.max(currentMax, index);
    }, 0);

    return `${localPart}+${maxIndex + 1}@${domain}`;
  };

  useEffect(() => {
    const fetchGet = async () => {
      try {
        const qrFile = await qrCreate(link, "/assets/images/app_logo.png");
        setQr(qrFile);
      } catch (error) {
        setErrorMessage(JSON.stringify(error));
      }
    };
    fetchGet();
  }, [link]);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const data = await checkQrStatus(code);
      if (cancelled) return;

      if (data?.error) {
        setErrorMessage(data.error);
        return;
      }

      if (data?.expired) {
        setErrorMessage(
          "Код входа истёк. Обновите страницу, чтобы получить новый QR-код.",
        );
        return;
      }

      setStatus(data || emptyStatus);
    };

    tick();
    const timer = window.setInterval(tick, 2500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [code, checkQrStatus]);

  useEffect(() => {
    if (!authProjects.length) {
      if (selectedProjectId) setSelectedProjectId("");
      return;
    }

    const selectedExists = authProjects.some(
      (project: any) =>
        String(project?.auth?._id || project?.auth?.email) ===
        selectedProjectId,
    );
    if (!selectedExists) {
      setSelectedProjectId(
        String(
          authProjects[0]?.auth?._id || authProjects[0]?.auth?.email || "",
        ),
      );
    }
  }, [status, selectedProjectId]);

  const continueWithAuth = async () => {
    if (!selectedAuth?.email || loading) return;

    setLoading(true);
    setErrorMessage("");

    try {
      const result = await signinByEmail(selectedAuth.email);
      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      rememberPwaProfile(selectedAuth.email);
      window.location.replace("/panel");
    } finally {
      setLoading(false);
    }
  };

  const continueWithDemoAgain = async () => {
    if (loading) return;

    const email = getNextDemoEmail(
      status?.userLogin || paidProject?.auth?.email || "",
    );
    if (!email) {
      setErrorMessage("Не удалось сформировать email для нового демо-проекта.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const result = await signupCaptcha(
        false,
        { email, promocode: status.agentId, subscriptions, user: status.user },
        utmCompany,
        status.agentId,
      );
      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      rememberPwaProfile(email);
      window.location.replace("/panel");
    } finally {
      setLoading(false);
    }
  };

  const continueWithSignup = async () => {
    if (loading) return;

    const email = status?.userLogin;
    if (!email) {
      setErrorMessage(
        "Не удалось получить данные пользователя из приложения. Повторите вход через QR-код.",
      );
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const result = await signupCaptcha(
        false,
        { email, promocode: status.agentId, subscriptions, user: status.user },
        utmCompany,
        status.agentId,
      );
      if (result?.error) {
        setErrorMessage(result.error);
        return;
      }

      rememberPwaProfile(email);
      window.location.replace("/panel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalError errorMessage={errorMessage} />

      <div className="container-page flex min-h-[82vh] items-center justify-center pb-16 pt-32">
        <section className="grid w-full max-w-5xl gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-center">
          <div>
            <p className="eyebrow">Единый аккаунт VCENTRE.IT</p>
            <h1 className="display-1 mt-5">
              Войти в <span className="text-primary">VCENTRE.ONE</span>
            </h1>
            <p className="lede mt-6 max-w-xl">
              Откройте приложение VCENTRE.IT и подтвердите вход.
            </p>
            <div className="mt-8 border-l-2 border-primary pl-5">
              <p className="display-2">Нет аккаунта?</p>
              <p className="body-text mt-2">
                Регистрация в приложении занимает один путь: E-mail → код из
                E-mail → аккаунт создан.
              </p>
            </div>
          </div>
          <div className="surface border-primary/30 text-center">
            <h2 className="display-3 mb-5">Подтвердите вход в приложении</h2>
            {!status?.entered ? (
              <p className="input-label text-center">
                Отсканируйте QR-код в приложении Vcentre.IT или камерой вашего
                смартфона
              </p>
            ) : (
              <></>
            )}

            {!status?.entered ? (
              <>
                <div className="flex place-content-center mt-5 mb-5">
                  {qr ? (
                    <img
                      src={qr}
                      alt="QR входа"
                      className="w-[240px] h-[240px] rounded bg-white p-2"
                    />
                  ) : (
                    <></>
                  )}
                </div>
                <Button
                  asChild
                  variant="site"
                  size="lg"
                  className="mb-5 w-full"
                >
                  <a href={link} target="_blank" rel="noopener noreferrer">
                    Открыть приложение
                  </a>
                </Button>
                <p className="input-label text-center">
                  Ожидаем подтверждение входа…
                </p>
              </>
            ) : hasAuth ? (
              <div className="flex flex-col gap-3">
                <p className="input-label">Вы входите в систему как:</p>
                <div className="rounded border border-[var(--color-border)] p-3">
                  <div className="flex items-center gap-3 mb-3">
                    {pwaAvatar ? (
                      <img
                        src={pwaAvatar}
                        alt={pwaName || pwaMail}
                        className="w-12 h-12 rounded-full object-cover bg-white"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[var(--color-border)]" />
                    )}
                    <div className="min-w-0">
                      {pwaName && (
                        <p className="input-input truncate">{pwaName}</p>
                      )}
                      {pwaMail && (
                        <p className="input-label truncate">{pwaMail}</p>
                      )}
                    </div>
                  </div>
                  {authProjects.length > 1 ? (
                    <div className="flex flex-col gap-2 mb-3">
                      <p className="input-label">Найдены проекты:</p>
                      {authProjects.map((project: any, index: number) => {
                        const projectKey = String(
                          project?.auth?._id || project?.auth?.email || index,
                        );
                        const isSelected =
                          projectKey ===
                          String(
                            selectedAuth?._id || selectedAuth?.email || "",
                          );
                        const projectName =
                          project?.pwaProps?.name ||
                          project?.auth?.name ||
                          project?.auth?.email ||
                          `Проект ${index + 1}`;

                        return (
                          <button
                            key={projectKey}
                            type="button"
                            className={`w-full rounded border p-2 text-left transition ${isSelected ? "border-[var(--color-brand)] bg-[var(--color-brand)]/10" : "border-[var(--color-border)] hover:border-[var(--color-brand)]"}`}
                            onClick={() => setSelectedProjectId(projectKey)}
                          >
                            <span className="block input-input truncate">
                              {projectName}
                            </span>
                            <span className="block input-label truncate">
                              {project?.auth?.email || "—"} ·{" "}
                              {project?.auth?.tarif || "—"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : selectedPwaProps?.name ? (
                    <p className="input-label mt-2">
                      У вас есть 1 проект: {selectedPwaProps.name}
                    </p>
                  ) : (
                    <></>
                  )}
                  <p className="input-label">Имя: {selectedAuth.name || "—"}</p>
                  <p className="input-label">
                    Тариф: {selectedAuth.tarif || "—"}
                  </p>
                  <p className="input-label">
                    Промокод: {status.agentId || "-"}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={loading}
                  className="button-brand inline-flex items-center gap-2 my-2 w-full"
                  onClick={continueWithAuth}
                >
                  {loading ? "Выполняем…" : "Продолжить"}
                </button>
                {hasPaidProject && (
                  <button
                    type="button"
                    disabled={loading}
                    className="button-border inline-flex items-center gap-2 w-full"
                    onClick={continueWithDemoAgain}
                  >
                    {loading ? "Создаём Демо…" : "Зарегистрировать ещё Демо"}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="input-label">Вы входите в систему как:</p>
                <div className="rounded border border-[var(--color-border)] p-2">
                  <div className="flex items-center gap-2">
                    {pwaAvatar ? (
                      <img
                        src={pwaAvatar}
                        alt={pwaName || pwaMail}
                        className="w-12 h-12 rounded-full object-cover bg-white"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[var(--color-border)]" />
                    )}
                    <div className="min-w-0">
                      {pwaName && (
                        <p className="input-input truncate">{pwaName}</p>
                      )}
                      {pwaMail && (
                        <p className="input-label truncate">{pwaMail}</p>
                      )}
                      {status.agentId && (
                        <p className="input-label truncate">
                          Промокод: {status.agentId || "-"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <p className="input-label text-center">
                  Профиль найден, аккаунт платформы ещё не создан.
                </p>
                <Checkbox
                  isSelected={informMessage}
                  isInvalid={!informMessage}
                  size="sm"
                  color="success"
                  classNames={{ base: "mt-0", label: "input-input" }}
                  onValueChange={setInformMessage}
                >
                  {SUBSCRIPTION_OPTIONS[0].reason}
                </Checkbox>
                {!informMessage && <p className="p-only-mini text-[var(--color-danger)]">
                  Не забудьте включить, чтобы не потерять нас!
                </p>}
                <Checkbox
                  isSelected={vebinarMessage}
                  isInvalid={!vebinarMessage}
                  size="sm"
                  color="success"
                  classNames={{ base: "mt-0", label: "input-input" }}
                  onValueChange={setVebinarMessage}
                >
                  {SUBSCRIPTION_OPTIONS[1].reason}
                </Checkbox>
                {!vebinarMessage && <p className="p-only-mini text-[var(--color-danger)]">
                  Не забудьте включить, чтобы не потерять нас!
                </p>}
                <button
                  type="button"
                  disabled={loading}
                  className="button-brand inline-flex items-center gap-2 my-2 w-full"
                  onClick={continueWithSignup}
                >
                  {loading ? "Создаём…" : "Продолжить"}
                </button>
                <p className="text-xs text-center text-muted-foreground font-montserrat leading-relaxed">
                  Нажимая кнопку «Продолжить», вы принимаете{" "}
                  <Link
                    href="/license"
                    target="_blank"
                    className="text-xs underline hover:no-underline"
                  >
                    лицензионное соглашение
                  </Link>{" "}
                  и даёте{" "}
                  <Link
                    href="/policy"
                    target="_blank"
                    className="text-xs underline hover:no-underline"
                  >
                    согласие
                  </Link>{" "}
                  на обработку персональных данных.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
